import { useMemo, useRef } from 'react';
import ReactQuill from 'react-quill-new';
import 'quill/dist/quill.snow.css';
import { completeUploadFile, presignUploadFile } from '@/api/file';
import { dispatchToast } from '@/components/ui/GlobalToast';

interface RichTextEditorProps {
  value?: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: number;
  maxPlainTextLength?: number;
}

const MAX_RICH_TEXT_IMAGE_BYTES = 5 * 1024 * 1024;

export function getPlainTextFromHtml(html: string): string {
  if (!html) return '';
  if (typeof document === 'undefined') {
    return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  }
  const div = document.createElement('div');
  div.innerHTML = html;
  return (div.textContent || div.innerText || '').replace(/\s+/g, ' ').trim();
}

/** 判断富文本是否有可展示内容（含纯文字或图片/视频等嵌入） */
export function hasRichHtmlContent(value: unknown): boolean {
  if (typeof value !== 'string') return false;
  const html = value.trim();
  if (!html) return false;
  if (/<img\b/i.test(html)) return true;
  if (/<(video|iframe|embed)\b/i.test(html)) return true;
  return getPlainTextFromHtml(html).length > 0;
}

function normalizeQuillValue(html: string): string {
  const plain = getPlainTextFromHtml(html);
  if (!plain) return '';
  return html.trim();
}

async function uploadRichTextImage(file: File): Promise<string> {
  if (!file.type.startsWith('image/')) {
    throw new Error('请选择图片文件');
  }
  if (file.size > MAX_RICH_TEXT_IMAGE_BYTES) {
    throw new Error('图片大小不能超过 5MB');
  }

  const presign = await presignUploadFile({
    fileName: file.name,
    contentType: file.type || 'image/png',
    fileCategory: 'other',
  });

  const uploadRes = await fetch(presign.uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': file.type || 'image/png',
    },
    body: file,
  });

  if (!uploadRes.ok) {
    throw new Error(`上传失败（HTTP ${uploadRes.status}）`);
  }

  await completeUploadFile({
    objectKey: presign.objectKey,
    originalName: file.name,
    mimeType: file.type || 'image/png',
    sizeBytes: file.size,
    fileCategory: 'other',
  });

  return `https://${presign.bucket}.${presign.endpoint}/${presign.objectKey}`;
}

export default function RichTextEditor({
  value = '',
  onChange,
  placeholder = '请输入内容...',
  minHeight = 140,
  maxPlainTextLength,
}: RichTextEditorProps) {
  const quillRef = useRef<ReactQuill>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadingRef = useRef(false);
  const imageHandlerRef = useRef<() => void>(() => {});

  const insertImageUrl = (url: string) => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;

    const range = editor.getSelection(true);
    const index = range?.index ?? editor.getLength();
    editor.insertEmbed(index, 'image', url);
    editor.setSelection(index + 1);
  };

  imageHandlerRef.current = () => {
    if (uploadingRef.current) {
      dispatchToast('info', '图片正在上传，请稍候');
      return;
    }
    fileInputRef.current?.click();
  };

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          ['bold', 'italic', 'underline', 'strike'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          ['link', 'image'],
          ['clean'],
        ],
        handlers: {
          image: () => imageHandlerRef.current(),
        },
      },
    }),
    [],
  );

  const formats = useMemo(
    () => ['bold', 'italic', 'underline', 'strike', 'list', 'link', 'image'],
    [],
  );

  const handleImageSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    if (uploadingRef.current) return;
    uploadingRef.current = true;
    dispatchToast('info', '正在上传图片...');

    try {
      const url = await uploadRichTextImage(file);
      insertImageUrl(url);
      dispatchToast('success', '图片上传成功');
    } catch (err) {
      dispatchToast('error', err instanceof Error ? err.message : '图片上传失败');
    } finally {
      uploadingRef.current = false;
    }
  };

  const plainTextLength = getPlainTextFromHtml(value || '');

  return (
    <div className="rich-text-editor">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleImageSelect}
      />
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value || ''}
        onChange={(content) => onChange(normalizeQuillValue(content))}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
      />
      <style>{`
        .rich-text-editor .ql-toolbar.ql-snow {
          min-height: auto;
        }
        .rich-text-editor .ql-editor {
          min-height: ${minHeight}px;
        }
      `}</style>
      {maxPlainTextLength ? (
        <div className="px-3 py-1.5 border-t border-gray-50 bg-gray-50/40">
          <p className="text-xs text-gray-400">
            已输入 {plainTextLength} 字，最多 {maxPlainTextLength} 字
          </p>
        </div>
      ) : null}
    </div>
  );
}
